import { useState } from 'react';
import {
  Award,
  BadgeCheck,
  CalendarClock,
  Footprints,
  Radar,
  Route,
  Timer,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { useGetApiV1StatisticsOverview } from '@/shared/api/generated/statistics/statistics';
import type { StatisticsCountDto } from '@/shared/api/generated/model';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Panel } from '@/shared/ui/Panel';
import { QueryError } from '@/shared/ui/QueryError';
import { StatCard } from '@/shared/ui/StatCard';
import { AreaChart } from '@/shared/ui/charts/AreaChart';
import { BarChart } from '@/shared/ui/charts/BarChart';

/**
 * The association's numbers (§19's statistics dashboards).
 *
 * ⚠️ **Two panels, not one grid, and the split is the point.** The first answers "in this window" and
 * moves when the dates move; the second answers "right now" and does not. Mixing them would let an
 * operator narrow the range to a month and read «١٢ متطوّعاً» as attrition — when the roster never
 * changed and only the question did.
 *
 * ⚠️ **The metric set is chosen, not specified.** §19 names "statistics dashboards" and nothing more.
 * These follow the three things the exports already answer — missions, volunteers, participation —
 * because those are the questions the association has asked in writing. Whatever it wants counted
 * instead is a change to this screen, not a rebuild of it.
 */
export function StatisticsPanel() {
  const { t, locale } = useLocale();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isPending, isError, error, refetch } = useGetApiV1StatisticsOverview({
    ...(from ? { from: new Date(from).toISOString() } : {}),
    ...(to ? { to: new Date(to).toISOString() } : {}),
  });

  const number = (value: number) => value.toLocaleString(locale === 'ar' ? 'ar-SA' : 'en-GB');

  /**
   * The month as it is READ, from the `yyyy-MM` key the server groups by.
   *
   * ⚠️ The digits are converted and the key is not. The server's key is what the two halves of a
   * chart line up on and it stays machine-readable; an axis reading `2025-08` beside a count reading
   * «٢» is the same page written in two alphabets. Converted rather than formatted through an Arabic
   * culture, for §15's reason: that would switch the calendar too, and «١٤٤٧-٠٢» is a different month.
   */
  const month = (key: string) =>
    locale === 'ar' ? key.replace(/[0-9]/g, (d) => String.fromCharCode(0x0660 + Number(d))) : key;

  return (
    <>
      <PageHeader
        title={t.statistics.heading}
        lede={t.statistics.lede}
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <Field label={t.statistics.from} htmlFor="statistics-from">
              <input
                id="statistics-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                className="rounded-md border border-border bg-surface-elevated px-3 py-2 outline-none focus:border-primary"
              />
            </Field>

            <Field label={t.statistics.to} htmlFor="statistics-to">
              <input
                id="statistics-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                className="rounded-md border border-border bg-surface-elevated px-3 py-2 outline-none focus:border-primary"
              />
            </Field>
          </div>
        }
      />

      {isError ? <QueryError error={error} onRetry={() => void refetch()} /> : null}

      {isPending || !data ? (
        <p className="text-sm text-muted-foreground">{t.statistics.loading}</p>
      ) : (
        <div className="flex flex-col gap-6">
          <Panel title={t.statistics.periodHeading} description={t.statistics.periodLede}>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <StatCard
                label={t.statistics.missionsOpened}
                value={number(data.missionsOpened)}
                icon={Radar}
              />
              <StatCard
                label={t.statistics.participationsCredited}
                value={number(data.participationsCredited)}
                icon={BadgeCheck}
              />
              <StatCard
                label={t.statistics.hoursOnTheGround}
                value={number(data.hoursOnTheGround)}
                icon={Timer}
              />
              <StatCard
                label={t.statistics.distanceCovered}
                value={number(data.distanceCoveredKm)}
                icon={Route}
              />
              <StatCard
                label={t.statistics.knightsAwarded}
                value={number(data.knightsAwarded)}
                icon={Award}
              />
              <StatCard
                label={t.statistics.certificatesIssued}
                value={number(data.certificatesIssued)}
                icon={Footprints}
              />
            </div>
          </Panel>

          <Panel title={t.statistics.byMonth} description={t.statistics.byMonthLede}>
            <AreaChart
              series={[
                {
                  name: t.statistics.missionsOpened,
                  data: data.missionsByMonth.map((point) => point.count),
                },
              ]}
              categories={data.missionsByMonth.map((point) => month(point.month))}
              formatValue={number}
            />
          </Panel>

          <div className="grid gap-6 xl:grid-cols-2">
            <Panel title={t.statistics.byOutcome}>
              <Breakdown
                rows={data.missionsByOutcome}
                labels={t.missionOutcome}
                name={t.statistics.missionsOpened}
                empty={t.statistics.noOutcomes}
                formatValue={number}
              />
            </Panel>

            <Panel title={t.statistics.byType}>
              <Breakdown
                rows={data.missionsByType}
                labels={t.missionType}
                name={t.statistics.missionsOpened}
                empty={t.statistics.noMissions}
                formatValue={number}
              />
            </Panel>
          </div>

          <Panel title={t.statistics.rosterHeading} description={t.statistics.rosterLede}>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard
                label={t.statistics.missionsRunning}
                value={number(data.missionsRunning)}
                icon={CalendarClock}
              />
              <StatCard
                label={t.statistics.volunteersApproved}
                value={number(data.volunteersApproved)}
                icon={UserCheck}
              />
              <StatCard
                label={t.statistics.volunteersAwaitingReview}
                value={number(data.volunteersAwaitingReview)}
                icon={UserPlus}
              />
            </div>

            <div className="mt-4">
              <Breakdown
                rows={data.volunteersByType}
                labels={t.volunteerType}
                name={t.statistics.volunteersApproved}
                empty={t.statistics.noVolunteers}
                formatValue={number}
              />
            </div>
          </Panel>
        </div>
      )}
    </>
  );
}

/**
 * A count per category.
 *
 * ⚠️ **Horizontal, because the categories are sentences.** §23.3's outcomes are quoted whole —
 * «عُثر على المفقود متوفّى» — and a label that long under a vertical bar is either rotated on its
 * side or clipped to three words, both of which lose the half that says who was found.
 */
function Breakdown({
  rows,
  labels,
  name,
  empty,
  formatValue,
}: {
  rows: readonly StatisticsCountDto[];
  labels: Record<string, string>;
  name: string;
  empty: string;
  formatValue: (value: number) => string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>;
  }

  // The API sends enum NAMES. An unknown key falls back to the raw value: if the server adds an
  // outcome before this panel knows about it, showing `Suspended` is honest and showing nothing
  // hides meaning.
  const named = rows.map((row) => ({ label: labels[row.key] ?? row.key, count: row.count }));

  return (
    <>
      <BarChart
        horizontal
        series={[{ name, data: named.map((row) => row.count) }]}
        categories={named.map((row) => row.label)}
        height={Math.max(180, rows.length * 46)}
        formatValue={formatValue}
      />

      {/*
        ⚠️ **The numbers in words beside the picture, not instead of it.**

        Two reasons, and neither is decoration. An Apex chart is an SVG of positioned `<text>` and
        `<path>` — a screen reader gets a pile of unordered fragments, so the chart alone leaves this
        panel unreadable to anybody using one. And a bar answers "which is bigger" but never "how
        many", which is the figure that goes into the association's own report.
      */}
      <dl className="mt-2 flex flex-col gap-1 text-sm">
        {named.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-4 border-b border-border pb-1 last:border-0">
            <dt className="min-w-0">{row.label}</dt>
            <dd className="tabular font-medium">{formatValue(row.count)}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}
